import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Card, Col, Icon, Layout, Loading, Row } from '@folio/stripes/components';
import { FormattedMessage } from 'react-intl';
import ReportsContext from '../../../../Context/reports/ReportsContext';
import { renderDisplayComponent } from '../../../reports/results/core/DisplayMethodRegistry';
import useCubeQuery from '../../../../hooks/useQuery';
import { getLocalizedValue } from '../../../../helpers/localizationUtils';

function SingleReportDisplay({ report, query, compact = false, namespace }) {
  const [resultSet, error, isLoading] = useCubeQuery(
    query,
    { namespace: namespace || `widget-report-${report?.id}` }
  );

  const getDisplayMethod = () => {
    return report?.visualization?.defaultDisplayMethod ||
      report?.defaultDisplayMethod ||
      'numLabel';
  };

  if (error) {
    return (
      <div style={{ padding: '1rem', textAlign: 'center', color: 'red' }}>
        <Icon icon="exclamation-circle" />
        <div>Error loading report</div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <Layout
        className="centered full"
        style={{ maxWidth: compact ? '10rem' : '15rem', height: compact ? '4rem' : '8rem' }}
      >
        <Loading size={compact ? 'medium' : 'xlarge'} />
      </Layout>
    );
  }

  if (!resultSet || !resultSet.loadResponses?.length) {
    return (
      <div style={{ padding: '1rem', textAlign: 'center' }}>
        <FormattedMessage id="ui-reports.widget.noData" defaultMessage="No data available" />
      </div>
    );
  }

  return (
    <div style={{ minHeight: compact ? '100px' : '200px' }}>
      {renderDisplayComponent(getDisplayMethod(), {
        resultSet,
        reportType: report?.reportType,
      })}
    </div>
  );
}

SingleReportDisplay.propTypes = {
  report: PropTypes.object.isRequired,
  query: PropTypes.object,
  compact: PropTypes.bool,
  namespace: PropTypes.string,
};

function MultiReportsWidget({ widget, widgetType = 'multiReports' }) {
  const reports = useContext(ReportsContext);
  const [reportsData, setReportsData] = useState([]);

  useEffect(() => {
    // New schema: widgetReportsConfig.reportIds, fallback to legacy reportsIds
    const reportIds = widget?.widgetReportsConfig?.reportIds || widget?.reportsIds || [];
    if (reportIds.length > 0 && reports.length > 0) {
      const widgetReports = reportIds
        .map(id => reports.find(r => r.id === id))
        .filter(Boolean);
      setReportsData(widgetReports);
    }
  }, [widget, reports]);

  const getQueryForReport = (report, index) => {
    // New schema: widgetReportsConfig.reportSettings[index].filters applied to report.queryMetadata
    return report?.queryMetadata ||
      widget?.queriesMetadata?.[index] ||
      null;
  };

  const renderReports = () => {
    if (!reportsData.length) return null;
    const colSize = Math.floor(12 / Math.min(reportsData.length, 3));

    return (
      <Row>
        {reportsData.map((report, index) => (
          <Col xs={12} sm={colSize} key={report.id}>
            <Card
              roundedBorder
              cardStyle={widgetType === 'comparison' ? 'default' : 'positive'}
              headerStart={
                <Icon icon="report">
                  {getLocalizedValue(report?.name)}
                </Icon>
              }
              marginBottom0
              style={{ marginBottom: '1rem' }}
            >
              <SingleReportDisplay
                report={report}
                query={getQueryForReport(report, index)}
                compact
                namespace={`widget-${widget?.id}-report-${report.id}`}
              />
            </Card>
          </Col>
        ))}
      </Row>
    );
  };

  return (
    <div style={{ padding: '1rem' }}>
      {renderReports()}
    </div>
  );
}

MultiReportsWidget.propTypes = {
  widget: PropTypes.object.isRequired,
  widgetType: PropTypes.string,
};

export default MultiReportsWidget;
